/**
 * HelpLink — inline link that opens the help drawer scoped to a
 * specific article. Used inside empty states to turn dead surfaces
 * into teaching moments without adding visible chrome elsewhere.
 *
 * Renders as a real ``<a href="/help/<slug>">`` so right-click
 * "Copy link" works and the link is keyboard-accessible. Click
 * preventDefault's and dispatches the same ``helpTarget:open`` event
 * the useHelpTarget hook fires on right-click — HelpOrb listens and
 * opens the panel scoped to the article without navigating away.
 *
 * Usage:
 *
 *   <div className="empty-state">
 *     No conversations yet. <HelpLink slug="asking-arc">Learn how Ask Arc works →</HelpLink>
 *   </div>
 */
// Inline styling so we don't need an extra CSS file for one small
// component. Subtle blue, underline on hover — looks like a link but
// not louder than the surrounding empty-state text.
const LINK_STYLE = {
  color: '#1976d2',
  textDecoration: 'none',
  fontWeight: 500,
  cursor: 'pointer',
};

export default function HelpLink({ slug, label = null, children, className = 'help-link' }) {
  function handleClick(e) {
    // Allow modifier-clicks (cmd/ctrl/shift) to fall through to native
    // behavior — opens the standalone /help page in a new tab, which
    // is what someone holding a modifier expects.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('helpTarget:open', {
      detail: { slug, label, question: null },
    }));
  }
  return (
    <a
      href={`/help/${slug}`}
      onClick={handleClick}
      className={className}
      style={LINK_STYLE}
      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
    >
      {children}
    </a>
  );
}
